/**
 * useFormDraft — DB-first autosave for in-progress form input.
 *
 * Drop this into any form/wizard whose unsaved input should survive a page
 * refresh. It keeps the database as the single source of truth: there is no
 * localStorage and no Zustand persistence. While the user types, the serialized
 * form state is debounced and written to the `form_drafts` table; on mount it
 * loads any existing draft and hands it back for hydration.
 *
 * Contract:
 *   1. Give it a stable `draftKey` and the current serializable `value`.
 *   2. Provide `onHydrate` — called once if a saved draft is found, so you can
 *      restore your local state from the DB.
 *   3. Call `discard()` after the form successfully commits its real entity.
 *
 * Everything is best-effort. If Supabase is down or the user is not signed in,
 * the hook quietly disables itself and the form behaves exactly as before.
 *
 * Usage:
 * ```tsx
 * const draft = useFormDraft<MyForm>({
 *   draftKey: 'submit-idea',
 *   value: { title, description },
 *   isEmpty: (v) => !v.title?.trim() && !v.description?.trim(),
 *   onHydrate: (v) => { setTitle(v.title ?? ''); setDescription(v.description ?? ''); },
 * });
 * // ...after a successful submit:
 * await draft.discard();
 * ```
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  loadFormDraft,
  saveFormDraft,
  discardFormDraft,
} from '../services/drafts/formDraftService';

export type FormDraftStatus = 'idle' | 'loading' | 'saving' | 'saved' | 'error';

export interface UseFormDraftOptions<T> {
  /** Stable identifier for this form instance, e.g. 'submit-idea'. */
  draftKey: string;
  /** Current serializable form state. */
  value: T;
  /**
   * Workspace the draft belongs to. Omit to use the current workspace; pass
   * `null` for forms that run before a workspace is selected.
   */
  workspaceId?: string | null;
  /** Turn persistence on/off (e.g. while permissions are still loading). Default true. */
  enabled?: boolean;
  /** Debounce window for autosave, in ms. Default 800. */
  debounceMs?: number;
  /** Called once when a saved draft is found on mount. Restore your state here. */
  onHydrate?: (payload: T) => void;
  /**
   * Return true when `value` is effectively empty. Empty values are never saved,
   * and an existing draft is discarded once the form is emptied out.
   */
  isEmpty?: (value: T) => boolean;
}

export interface UseFormDraftResult {
  status: FormDraftStatus;
  /** True once the initial load attempt has completed. */
  isLoaded: boolean;
  /** True when a saved draft was found and hydrated on mount. */
  hydratedFromDraft: boolean;
  /** Timestamp of the last successful save. */
  lastSavedAt: Date | null;
  /** Force an immediate save (e.g. on field blur). */
  saveNow: () => Promise<void>;
  /** Delete the draft. Call after the form commits its real entity. */
  discard: () => Promise<void>;
}

export function useFormDraft<T>(options: UseFormDraftOptions<T>): UseFormDraftResult {
  const {
    draftKey,
    value,
    workspaceId,
    enabled = true,
    debounceMs = 800,
    onHydrate,
    isEmpty,
  } = options;

  const [status, setStatus] = useState<FormDraftStatus>('idle');
  const [isLoaded, setIsLoaded] = useState(false);
  const [hydratedFromDraft, setHydratedFromDraft] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  // Keep the latest value/callbacks in refs so timers and unmount logic don't go stale.
  const valueRef = useRef(value);
  valueRef.current = value;
  const onHydrateRef = useRef(onHydrate);
  onHydrateRef.current = onHydrate;
  const isEmptyRef = useRef(isEmpty);
  isEmptyRef.current = isEmpty;

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Serialized snapshot of what's already persisted, to avoid redundant writes.
  const lastPersistedRef = useRef<string | null>(null);
  // Guards autosave until the initial load resolves (so we don't clobber a draft).
  const readyRef = useRef(false);

  const scope = { draftKey, workspaceId };
  const scopeRef = useRef(scope);
  scopeRef.current = scope;

  const persist = useCallback(async () => {
    if (!enabled) return;
    const current = valueRef.current;
    const serialized = JSON.stringify(current ?? null);

    const empty = isEmptyRef.current ? isEmptyRef.current(current) : false;

    // Nothing changed since the last write — skip.
    if (serialized === lastPersistedRef.current) return;

    if (empty) {
      // Form was cleared out: drop any saved draft so a refresh starts clean.
      if (lastPersistedRef.current !== null && lastPersistedRef.current !== JSON.stringify(null)) {
        await discardFormDraft(scopeRef.current);
      }
      lastPersistedRef.current = serialized;
      return;
    }

    setStatus('saving');
    const id = await saveFormDraft(scopeRef.current, current);
    if (id) {
      lastPersistedRef.current = serialized;
      setLastSavedAt(new Date());
      setStatus('saved');
    } else {
      setStatus('error');
    }
  }, [enabled]);

  // Initial load — hydrate from the DB if a draft exists.
  useEffect(() => {
    if (!enabled) {
      setIsLoaded(true);
      readyRef.current = true;
      return;
    }
    let cancelled = false;
    setStatus('loading');
    void (async () => {
      const draft = await loadFormDraft<T>(scopeRef.current);
      if (cancelled) return;
      if (draft && draft.payload != null) {
        lastPersistedRef.current = JSON.stringify(draft.payload);
        setHydratedFromDraft(true);
        onHydrateRef.current?.(draft.payload);
      }
      setIsLoaded(true);
      setStatus('idle');
      readyRef.current = true;
    })();
    return () => {
      cancelled = true;
    };
    // Re-run if the form identity changes.
  }, [draftKey, workspaceId, enabled]);

  // Serialize so deep value changes are detected by the effect below without
  // putting a complex expression directly in the dependency array.
  const serializedValue = JSON.stringify(value ?? null);

  // Debounced autosave on value changes.
  useEffect(() => {
    if (!enabled || !readyRef.current) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      void persist();
    }, debounceMs);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [serializedValue, enabled, debounceMs, persist]);

  const saveNow = useCallback(async () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    await persist();
  }, [persist]);

  const discard = useCallback(async () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    lastPersistedRef.current = JSON.stringify(null);
    await discardFormDraft(scopeRef.current);
    setHydratedFromDraft(false);
    setLastSavedAt(null);
    setStatus('idle');
  }, []);

  return {
    status,
    isLoaded,
    hydratedFromDraft,
    lastSavedAt,
    saveNow,
    discard,
  };
}

export default useFormDraft;
